import { motion } from "framer-motion";
import { ArrowUpRight, Palette, Layers } from "lucide-react";

const LINKS = [
  {
    icon: Palette,
    title: "Polytec Colour Range",
    desc: "Explore the full range of colours, woodgrains and finishes we build with.",
    href: "https://www.polytec.com.au/colours/",
    testid: "materials-link-colours",
  },
  {
    icon: Layers,
    title: "Polytec Products",
    desc: "Boards, laminates, doors and panels — the materials behind every cabinet.",
    href: "https://www.polytec.com.au/products/",
    testid: "materials-link-products",
  },
];

export const Materials = () => (
  <section id="materials" data-testid="materials-section" className="py-24 lg:py-32 bg-espresso-800 border-y border-espresso-600">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
      >
        <p className="section-label" data-testid="materials-label">Materials & Suppliers</p>
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-cream mt-4" data-testid="materials-heading">
          Built with <span className="italic text-brass">polytec</span>
        </h2>
        <p className="text-sm text-cream-muted leading-relaxed mt-6 max-w-md">
          Polytec is our main supplier — Australian-made decorative boards, laminates and
          finishes form the heart of every kitchen, wardrobe and cabinet we machine.
          Browse their ranges to find the exact look you want, and we'll build it.
        </p>
        <p className="text-xs text-cream-muted/70 mt-4 max-w-md">
          Seen a colour you love? Note the name and include it in your quote enquiry.
        </p>
      </motion.div>
      <div className="space-y-5">
        {LINKS.map((l, i) => (
          <motion.a
            key={l.href}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={l.testid}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: i * 0.1 }}
            className="group flex items-center gap-6 border border-espresso-600 bg-espresso-900 p-7 hover:border-brass/60 transition-colors"
          >
            <span className="w-12 h-12 border border-brass/40 flex items-center justify-center shrink-0 group-hover:bg-brass/10 transition-colors">
              <l.icon size={22} className="text-brass" />
            </span>
            <span className="flex-1">
              <span className="font-serif text-lg text-cream block">{l.title}</span>
              <span className="text-xs text-cream-muted mt-1 block leading-relaxed">{l.desc}</span>
            </span>
            <ArrowUpRight size={20} className="text-espresso-600 group-hover:text-brass transition-colors shrink-0" />
          </motion.a>
        ))}
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 mt-20 pt-20 border-t border-espresso-600 items-center">
        <motion.figure
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          data-testid="wall-panels-figure"
          className="relative overflow-hidden border border-espresso-600"
        >
          <img
            src="/wall-panels.jpg"
            alt="Wall panel sample board — fluted timber, stone and marble-look finishes at Golden Mile Interiors"
            loading="lazy"
            className="w-full h-auto block"
          />
          <figcaption className="absolute bottom-0 inset-x-0 bg-[#120E0B]/85 backdrop-blur-md border-t border-espresso-600 px-5 py-3">
            <p className="text-xs text-cream-muted tracking-wide">Sample board in our Delacombe showroom</p>
          </figcaption>
        </motion.figure>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <p className="section-label" data-testid="wall-panels-label">Wall Panels</p>
          <h3 className="font-serif text-2xl sm:text-3xl tracking-tight text-cream mt-4" data-testid="wall-panels-heading">
            Uplift your walls <span className="italic text-brass">and interiors</span>
          </h3>
          <p className="text-sm text-cream-muted leading-relaxed mt-6 max-w-md">
            Choose from a wide range of wall panel options — fluted timber, slatted
            woodgrains, stone and marble-look finishes — to transform feature walls,
            TV surrounds, entryways and whole rooms.
          </p>
          <p className="text-sm text-cream-muted leading-relaxed mt-4 max-w-md">
            Every panel is machined to size in our workshop and can be supplied
            assembled or flat-packed for tradies and DIYers.
          </p>
          <a
            href="#quote"
            data-testid="wall-panels-cta"
            className="inline-flex items-center gap-2 border border-brass/50 text-brass text-sm px-6 py-3 mt-8 hover:bg-brass hover:text-espresso-900 transition-colors"
          >
            Enquire About Wall Panels <ArrowUpRight size={15} />
          </a>
        </motion.div>
      </div>
    </div>
  </section>
);
